import {
  getLeftSettingsMenuConfigForWorkspaceMode,
  getTodayButton,
  getToggleCollapseMenuItems,
  getZoomControls,
  timelineResetZoom,
  timelineZoomIn,
  timelineZoomOut,
} from './settingsBar/utils';
import {
  DisabledZoomControls,
  SettingsConfig,
  ViewContext,
} from '@ardoq/view-settings';
import {
  CollapsedBooleanProperties,
  CollapsibleIds,
  GridPrecisionOptionsData,
  TimelineItemModel,
  TimelineSettingsBounds,
  TimelineSettingsControlsDependencies,
  TimelineSettingsLabels,
  TimelineRenderingState,
  TimelineViewCommands,
  TimelineViewFieldsRegistry,
  TimelineViewSettings,
  WindowScalingLimitsForGrid,
} from '../types';
import { returnNull } from '@ardoq/common-helpers';
import { viewLegendCommands } from '@ardoq/view-legend';
import { TimeRange } from '@ardoq/graph';
import { TimelinesGridPrecisionUnit, ViewIds } from '@ardoq/api-types';
import type { I18nLocale } from '@ardoq/i18n';
import { buildTimelineSettingsLabels } from '../i18n';
import {
  getCollapsedBooleanProperties,
  mergeCollapsedBooleanProperties,
} from '../data/calculations';
import { toTimelineSettingsBounds } from '../settingsDependencies';
import {
  getDisabledOptionsPopoverContentForCoreApp,
  getGridPrecisionOptionsData,
} from './chartWindow/timeFrameSelection';
import { getMultiselectFieldOptions } from './fields/multiselectFieldMenu';
import { getMilestoneShapeForFieldIndex } from '../items/milestones/milestone';
import { DropdownItem, Notifier } from '@ardoq/dropdown-menu';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { isEqual } from 'lodash';
import { OnViewSettingsUpdate } from '@ardoq/data-model';

type GetTimelineViewContextArgs = {
  windowCenter: [number, number];
  windowScale: number;
  scalingLimitsForAdjustedGrid: WindowScalingLimitsForGrid;
  availableExpandedDomain: TimeRange;
  viewInstanceId: string;
  isLegendActive: boolean;
  getTimelineContentContainer: () => HTMLDivElement | null;
  labels: TimelineSettingsLabels;
  commands: TimelineViewCommands;
};
const getTimelineViewContext = ({
  windowCenter,
  windowScale,
  scalingLimitsForAdjustedGrid,
  availableExpandedDomain,
  viewInstanceId,
  isLegendActive,
  getTimelineContentContainer,
  labels,
  commands,
}: GetTimelineViewContextArgs): ViewContext => {
  const zoomArgs = {
    windowCenter,
    windowScale,
    windowScalingLimitsForGrid: scalingLimitsForAdjustedGrid,
    viewInstanceId,
    commands,
    zoomInLabel: labels.zoomIn,
    zoomOutLabel: labels.zoomOut,
    resetZoomLabel: labels.resetZoom,
  };
  const zoomControlButtons = getZoomControls(zoomArgs);
  const todayButton = getTodayButton({
    chartDomainRange: availableExpandedDomain,
    windowScale,
    currentWindowCenter: windowCenter,
    viewInstanceId,
    commands,
    todayLabel: labels.today,
    todayNotInRangePopover: labels.todayNotInRangePopover,
    noScrollPossiblePopover: labels.noScrollPossiblePopover,
  });
  const zoomControls = [
    ...zoomControlButtons,
    todayButton,
  ] satisfies SettingsConfig[];

  return {
    getContainer: getTimelineContentContainer,
    getGraphComponent: returnNull,
    zoomIn: timelineZoomIn(zoomArgs),
    zoomOut: timelineZoomOut(zoomArgs),
    optimizeLayout: timelineResetZoom(zoomArgs),
    toggleLegend: () =>
      viewLegendCommands.updateViewLegendSettings({
        viewId: ViewIds.TIMELINE,
        isActive: !isLegendActive,
      }),
    isLegendActive,
    zoomControls,
    zoomControlsDisabledState: DisabledZoomControls.NONE,
    viewInstanceId,
    knowledgeBaseLink: KnowledgeBaseLink.TIMELINE,
  };
};

type LeftMenuOptionsData = {
  gridPrecisionOptionsData: GridPrecisionOptionsData;
  milestoneFieldOptions: DropdownItem[];
  phaseFieldOptions: DropdownItem[];
  collapsedBooleanProperties: CollapsedBooleanProperties;
};

type GetViewModifiersOptionsDataArgs = {
  viewInstanceId: string;
  viewSettings: TimelineViewSettings;
  adjustedGridPrecision: TimelinesGridPrecisionUnit;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  collapsedGroupIds: Set<string>;
  collapsedFieldDataIds: Set<string>;
  collapsibleGroups: CollapsibleIds;
  collapsibleFieldDataIds: CollapsibleIds;
  settingsBounds: TimelineSettingsBounds;
  labels?: TimelineSettingsLabels;
  commands: TimelineViewCommands;
};

const getViewModifiersOptionsData = ({
  viewInstanceId,
  viewSettings,
  adjustedGridPrecision,
  onViewSettingsUpdate,
  collapsedGroupIds,
  collapsedFieldDataIds,
  collapsibleGroups,
  collapsibleFieldDataIds,
  settingsBounds,
  labels = {},
  commands,
}: GetViewModifiersOptionsDataArgs): LeftMenuOptionsData => {
  const collapsedGroupsBooleanProperties = getCollapsedBooleanProperties({
    collapsedIds: collapsedGroupIds,
    collapsibleIds: collapsibleGroups,
  });

  const collapsedFieldDataBooleanProperties = getCollapsedBooleanProperties({
    collapsedIds: collapsedFieldDataIds,
    collapsibleIds: collapsibleFieldDataIds,
  });

  const mergedCollapsedBooleanProperties: CollapsedBooleanProperties =
    mergeCollapsedBooleanProperties(
      collapsedGroupsBooleanProperties,
      collapsedFieldDataBooleanProperties
    );

  const gridPrecisionOptionLabels = {
    autoLayout: labels.gridPrecisionAutoLayout,
    daily: labels.gridPrecisionDaily,
    weekly: labels.gridPrecisionWeekly,
    monthly: labels.gridPrecisionMonthly,
    quarterly: labels.gridPrecisionQuarterly,
    yearly: labels.gridPrecisionYearly,
  };

  const gridPrecisionOptionsData = getGridPrecisionOptionsData({
    viewSettings,
    viewInstanceId,
    commands,
    adjustedGridPrecision,
    availableGridPrecisions: settingsBounds.availableGridPrecisions,
    onViewSettingsUpdate,
    getDisabledOptionsPopoverContent:
      labels.getDisabledOptionsPopoverContent ??
      getDisabledOptionsPopoverContentForCoreApp,
    gridPrecisionOptionLabels,
    autoLayoutInfoPopover: labels.autoLayoutInfoPopover,
  });

  const phaseFieldOptions = getMultiselectFieldOptions({
    fields: settingsBounds.phaseFields,
    selectedFieldIds: viewSettings.selectedPhaseFields,
    viewSettingPropertyName: 'selectedPhaseFields',
    onViewSettingsUpdate,
    hideAllLabel: labels.hideAll,
  });
  const milestoneFieldOptions = getMultiselectFieldOptions({
    fields: settingsBounds.milestoneFields,
    selectedFieldIds: viewSettings.selectedMilestoneFields,
    viewSettingPropertyName: 'selectedMilestoneFields',
    onViewSettingsUpdate,
    getIconName: index => getMilestoneShapeForFieldIndex(index),
    hideAllLabel: labels.hideAll,
  });

  return {
    collapsedBooleanProperties: mergedCollapsedBooleanProperties,
    gridPrecisionOptionsData,
    phaseFieldOptions,
    milestoneFieldOptions,
  };
};

type ViewpointModeArgs = {
  commands: TimelineViewCommands;
  adjustedGridPrecision: TimelinesGridPrecisionUnit;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  collapsedGroupIds: Set<string>;
  collapsedFieldDataIds: Set<string>;
  timelineViewFieldsRegistry: TimelineViewFieldsRegistry;
  collapsibleGroups: CollapsibleIds;
  collapsibleFieldDataIds: CollapsibleIds;
  viewSettings: TimelineViewSettings;
  viewInstanceId: string;
  availableExpandedDomain: TimeRange;
  scalingLimitsForAdjustedGrid: WindowScalingLimitsForGrid;
  windowCenter: [number, number];
  windowScale: number;
  viewModelData: TimelineItemModel[];
  viewModelDataDomain: [Date, Date];
  getTimelineContentContainer: () => HTMLDivElement | null;
  i18nLocale: I18nLocale;
};

const getMenuAndContextArgsForViewpointMode = (
  viewModelState: TimelineRenderingState
): ViewpointModeArgs => {
  const {
    commands,
    viewSettings,
    viewModelSourceData: {
      viewInstanceId,
      i18nLocale,
      viewInterface: {
        timelineCallbacks: { onViewSettingsUpdate },
      },
    },
    timelineViewRenderingData: {
      collapsedGroupIds,
      collapsedFieldDataIds,
      timelineChartRenderingData: {
        timelineChartRangeConstraints: {
          availableExpandedDomain,
          scalingLimitsForAdjustedGrid,
        },
        timelineChartProperties: {
          chartWindow: { windowCenter, windowScale },
          adjustedGridPrecision,
        },
      },
    },
    timelineViewModelData: {
      viewModelData,
      viewModelDataDomain,
      timelineViewFieldsRegistry,
      collapsibleGroups,
      collapsibleFieldDataIds,
    },
    getTimelineContentContainer,
  } = viewModelState;

  return {
    commands,
    adjustedGridPrecision,
    onViewSettingsUpdate,
    collapsedGroupIds,
    collapsedFieldDataIds,
    timelineViewFieldsRegistry,
    collapsibleGroups,
    collapsibleFieldDataIds,
    viewSettings,
    viewInstanceId,
    availableExpandedDomain,
    scalingLimitsForAdjustedGrid,
    windowCenter,
    windowScale,
    viewModelData,
    viewModelDataDomain,
    getTimelineContentContainer,
    i18nLocale,
  };
};

export const buildSettingsControlsForViewpointMode = (
  previousState: TimelineRenderingState,
  viewModelState: TimelineRenderingState
): TimelineSettingsControlsDependencies | null => {
  const previousArgs = getMenuAndContextArgsForViewpointMode(previousState);
  const currentArgs = getMenuAndContextArgsForViewpointMode(viewModelState);

  if (isEqual(previousArgs, currentArgs)) {
    return null;
  }

  const {
    adjustedGridPrecision,
    onViewSettingsUpdate,
    collapsedGroupIds,
    collapsedFieldDataIds,
    collapsibleGroups,
    collapsibleFieldDataIds,
    viewSettings,
    viewInstanceId,
    availableExpandedDomain,
    scalingLimitsForAdjustedGrid,
    windowCenter,
    windowScale,
    viewModelData,
    getTimelineContentContainer,
    i18nLocale,
    commands,
  } = currentArgs;

  const labels = buildTimelineSettingsLabels(i18nLocale);

  const settingsBounds = toTimelineSettingsBounds({
    timelineViewModelData: viewModelState.timelineViewModelData,
    availableExpandedDomain,
  });

  const {
    collapsedBooleanProperties,
    gridPrecisionOptionsData,
    milestoneFieldOptions,
    phaseFieldOptions,
  } = getViewModifiersOptionsData({
    viewInstanceId,
    viewSettings,
    adjustedGridPrecision,
    onViewSettingsUpdate,
    collapsedGroupIds,
    collapsedFieldDataIds,
    collapsibleGroups,
    collapsibleFieldDataIds,
    settingsBounds,
    labels,
    commands,
  });

  const leftMenu = getToggleCollapseMenuItems({
    collapsedBooleanProperties,
    viewInstanceId,
    commands,
    collapseAllLabel: labels.collapseAll,
    expandAllLabel: labels.expandAll,
  });

  const isLegendActive = viewSettings.isLegendActive;

  const viewContext = getTimelineViewContext({
    windowCenter,
    windowScale,
    scalingLimitsForAdjustedGrid,
    availableExpandedDomain,
    viewInstanceId,
    isLegendActive,
    getTimelineContentContainer,
    labels,
    commands,
  });

  return {
    ...settingsBounds,
    viewInstanceId,
    viewModifiers: leftMenu,
    viewSettings,
    viewContext,
    isEmptyView: viewModelData.length === 0,
    gridPrecisionOptionsData,
    milestoneFieldOptions,
    phaseFieldOptions,
  };
};

type WorkspaceModeArgs = {
  commands: TimelineViewCommands;
  adjustedGridPrecision: TimelinesGridPrecisionUnit;
  collapsedGroupIds: Set<string>;
  collapsedFieldDataIds: Set<string>;
  collapsibleGroups: CollapsibleIds;
  collapsibleFieldDataIds: CollapsibleIds;
  viewSettings: TimelineViewSettings;
  hasNewGroupingFeature: boolean | undefined;
  isPresentationMode: () => boolean;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  openDateRangeViewSetting: Notifier;
  openMilestonesViewSetting: Notifier;
  viewInstanceId: string;
  availableExpandedDomain: TimeRange;
  scalingLimitsForAdjustedGrid: WindowScalingLimitsForGrid;
  windowCenter: [number, number];
  windowScale: number;
  timelineViewFieldsRegistry: TimelineViewFieldsRegistry;
  viewModelData: TimelineItemModel[];
  viewModelDataDomain: [Date, Date];
  getTimelineContentContainer: () => HTMLDivElement | null;
  i18nLocale: I18nLocale;
};

const getMenuAndContextArgsForWorkspaceMode = (
  viewModelState: TimelineRenderingState
): WorkspaceModeArgs => {
  const {
    commands,
    viewSettings,
    viewModelSourceData: {
      viewInstanceId,
      i18nLocale,
      viewInterface: {
        features: { hasNewGroupingFeature },
        timelineCallbacks: {
          isPresentationMode,
          onViewSettingsUpdate,
          openDateRangeViewSetting,
          openMilestonesViewSetting,
        },
      },
    },
    timelineViewRenderingData: {
      collapsedGroupIds,
      collapsedFieldDataIds,
      timelineChartRenderingData: {
        timelineChartRangeConstraints: {
          availableExpandedDomain,
          scalingLimitsForAdjustedGrid,
        },
        timelineChartProperties: {
          chartWindow: { windowCenter, windowScale },
          adjustedGridPrecision,
        },
      },
    },
    timelineViewModelData: {
      timelineViewFieldsRegistry,
      viewModelData,
      viewModelDataDomain,
      collapsibleGroups,
      collapsibleFieldDataIds,
    },
    getTimelineContentContainer,
  } = viewModelState;

  return {
    commands,
    adjustedGridPrecision,
    collapsedGroupIds,
    collapsedFieldDataIds,
    collapsibleGroups,
    collapsibleFieldDataIds,
    viewSettings,
    hasNewGroupingFeature,
    isPresentationMode,
    onViewSettingsUpdate,
    openDateRangeViewSetting,
    openMilestonesViewSetting,
    viewInstanceId,
    availableExpandedDomain,
    scalingLimitsForAdjustedGrid,
    windowCenter,
    windowScale,
    timelineViewFieldsRegistry,
    viewModelData,
    viewModelDataDomain,
    getTimelineContentContainer,
    i18nLocale,
  };
};

export const buildSettingsControlsForWorkspaceMode = (
  previousState: TimelineRenderingState,
  viewModelState: TimelineRenderingState
): TimelineSettingsControlsDependencies | null => {
  const previousArgs = getMenuAndContextArgsForWorkspaceMode(previousState);
  const currentArgs = getMenuAndContextArgsForWorkspaceMode(viewModelState);

  if (isEqual(previousArgs, currentArgs)) {
    return null;
  }

  const {
    adjustedGridPrecision,
    collapsedGroupIds,
    collapsedFieldDataIds,
    collapsibleGroups,
    collapsibleFieldDataIds,
    viewSettings,
    hasNewGroupingFeature,
    isPresentationMode,
    onViewSettingsUpdate,
    openDateRangeViewSetting,
    openMilestonesViewSetting,
    viewInstanceId,
    availableExpandedDomain,
    scalingLimitsForAdjustedGrid,
    windowCenter,
    windowScale,
    timelineViewFieldsRegistry,
    viewModelData,
    getTimelineContentContainer,
    i18nLocale,
    commands,
  } = currentArgs;

  const labels = buildTimelineSettingsLabels(i18nLocale);

  const settingsBounds = toTimelineSettingsBounds({
    timelineViewModelData: viewModelState.timelineViewModelData,
    availableExpandedDomain,
  });

  const {
    collapsedBooleanProperties,
    gridPrecisionOptionsData,
    milestoneFieldOptions,
    phaseFieldOptions,
  } = getViewModifiersOptionsData({
    viewInstanceId,
    viewSettings,
    adjustedGridPrecision,
    onViewSettingsUpdate,
    collapsedGroupIds,
    collapsedFieldDataIds,
    collapsibleGroups,
    collapsibleFieldDataIds,
    settingsBounds,
    labels,
    commands,
  });

  const leftMenu = getLeftSettingsMenuConfigForWorkspaceMode({
    gridPrecisionOptionsData,
    phaseFieldOptions,
    milestoneFieldOptions,
    viewSettings,
    timelineViewFieldsRegistry,
    onViewSettingsUpdate,
    toggleCollapseMenuItems: getToggleCollapseMenuItems({
      collapsedBooleanProperties,
      viewInstanceId,
      commands,
      collapseAllLabel: labels.collapseAll,
      expandAllLabel: labels.expandAll,
    }),
    isPresentationMode: Boolean(isPresentationMode?.()),
    hasNewGrouping: Boolean(hasNewGroupingFeature),
    openDateRangeViewSetting,
    openMilestonesViewSetting,
    selectTimelineLayoutLabel: labels.selectTimelineLayout,
    useNewGroupingLabel: labels.useNewGrouping,
    selectPhasesLabel: labels.selectPhases,
    showMilestonesLabel: labels.showMilestones,
    dateRangeLabel: labels.dateRangeLabel,
    startDatePlaceholder: labels.startDatePlaceholder,
    endDatePlaceholder: labels.endDatePlaceholder,
    timelineLayoutSelectLabel: labels.timelineLayoutSelectLabel,
  });

  const isLegendActive = viewSettings.isLegendActive;

  const viewContext = getTimelineViewContext({
    windowCenter,
    windowScale,
    scalingLimitsForAdjustedGrid,
    availableExpandedDomain,
    viewInstanceId,
    isLegendActive,
    getTimelineContentContainer,
    labels,
    commands,
  });

  return {
    ...settingsBounds,
    viewInstanceId,
    viewModifiers: leftMenu,
    viewSettings,
    viewContext,
    isEmptyView: viewModelData.length === 0,
    gridPrecisionOptionsData,
    phaseFieldOptions,
    milestoneFieldOptions,
  };
};
